<template>
  <tr class="cte-row" :class="rowStatusClass">
    <!-- date -->
    <td>
      <div class="cte-date">
        <p class="uk-margin-remove">{{ row.date?.replace('/2026 at ', ' ').replace(' PDT', '').replace(' PST', '') }}</p>
        <span
          v-if="row.collapsedCount > 0"
          class="cte-collapse-icon"
          :uk-tooltip="collapseTooltip"
        >
          <font-awesome-icon icon="circle-info" />
        </span>
      </div>
    </td>

    <!-- container status -->
    <td>
      <span 
        class="uk-label"
        :class="[hasFieldChangedClass('yard_status'), getIsAvailable() ? 'uk-label-success' : 'uk-label-danger']">
        {{ getIsAvailable() ? "YES":"NO" }}
      </span>
    </td>

    <!-- container location (aka yard status) -->
    <td>
      <p :class="hasFieldChangedClass('yard_status')">{{ getYardLocation() }}</p>
    </td>

    <!-- customs hold -->
    <td v-if="isClearedCustoms(row.event['customs'])">
      <p>
        <span class="uk-label uk-label-success" :class="hasFieldChangedClass('customs')">{{ row.event['customs'] }}</span>
      </p>
    </td>
    <td v-else>
      <p
        :class="hasFieldChangedClass('customs')"
        v-if="
          row.event['customs'].includes('Held') ||
          row.event['customs'].includes('NONE') ||
          row.event['customs'].includes('HELD') ||
          row.event['customs'].includes('HOLD') ||
          row.event['customs'].includes('NONE') ||
          row.event['customs'].includes('CUST EXAM')
        "
      >
        <span class="uk-label uk-label-danger">{{
          row.event['customs']
        }}</span>
      </p>
      <p v-else class="uk-label uk-label-success" :class="hasFieldChangedClass('customs')">{{ row.event['customs'] }}</p>
    </td>

    <!-- line hold -->
    <td>
      <p
        :class="hasFieldChangedClass('line')"
        v-if="
          row.event['line'].includes('Held') ||
          row.event['line'].includes('Hold') ||
          row.event['line'].includes('NONE')
        "
      >
        <span class="uk-label uk-label-danger">{{ row.event['line'] }}</span>
      </p>
      <p v-else-if="row.event['line'].includes('PAID')">
        <span class="uk-label uk-label-success" :class="hasFieldChangedClass('line')">{{ row.event['line'] }}</span>
      </p>
      <p v-else-if="row.event['line'].includes('Released')">
        <span class="uk-label uk-label-success" :class="hasFieldChangedClass('line')">{{ row.event['line'] }}</span>
      </p>
      <p v-else class="uk-label uk-label-success" :class="hasFieldChangedClass('line')">{{ row.event['line'] }}</p>
    </td>

    <!-- terminal hold -->
    <td>
      <!-- if terminal hold is not empty, get the "changed class" else, just don't bother to highlight it as "changed" -->
      <!-- No or None means success, everything else means some sort of hold exists (i.e. style as danger/red)-->
      <span
        class="uk-label"
        :class="
          [
            row.event['terminal'] ? hasFieldChangedClass('terminal') : '',
            ['no', 'none'].includes(row.event['terminal'].trim().toLowerCase()) ? 'uk-label-success' : 'uk-label-danger'
          ]">
        {{ row.event['terminal'] }}
      </span>
    </td>

    <!-- usda hold -->
    <td>
      <p :class="hasFieldChangedClass('usda')">{{ row.event['usda'] }}</p>
    </td>

    <!-- other hold info -->
    <td>
      ...
    </td>

    <!-- demurrage hold -->
    <td>
      <p :class="hasFieldChangedClass('demurrage_amount')">{{ row.event['demurrage_amount'] }}</p>
    </td>

    <!-- last free day -->
    <td>
      <p :class="hasFieldChangedClass('demurrage_lfd')">{{ row.event['demurrage_lfd'] }}</p>
    </td>
  </tr>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'ContainerTerminalEventsTableRowV1',
  props: {
    // A v1 display row: { version, event, date, collapsedCount, collapseDiffs }
    row: {
      type: Object,
      required: true,
    },
    // The adjacent OLDER v1 display row, used to highlight changed values; null
    // when there is none (oldest row, or the older neighbor is a v2 row).
    previousRow: {
      type: Object,
      required: false,
      default: null,
    },
    terminal: {
      type: String,
      required: true,
    },
  },
  methods: {
    normalizeUpper(value: any): string {
      if (value === null || value === undefined) return '';
      return String(value).trim().toUpperCase();
    },
    getIsAvailable() {
      if (!this.row?.event) {
        return false;
      }
      
      const yardStatusLower = this.row.event['yard_status'].toLowerCase();
      // some old scrapes contain ONLY 'Yes' 'No' or "off-dock"
      if (yardStatusLower.includes('available')) {
        return !yardStatusLower.includes('not available');
      }
      if (yardStatusLower.startsWith('yes')) {
        return true;
      }
      if (yardStatusLower.startsWith('no')) {
        return false;
      }
      // some legacy historical scrape: 2020-2023 (see container_event_v1_field_values.md)
      if (yardStatusLower.startsWith('off-dock')) {
        return false;
      }
      return false;
    },
    
    getYardLocation() {
      const yardStatus = this.row.event['yard_status'];
      // some old scrapes contain ONLY 'Yes' 'No' or "off-dock"
      // regular scrapes start with "available" or "not available"
      return yardStatus.replace(/^((not )?available|yes|no)/i, '');
    },
    isNegativeStatus(value: any): boolean {
      const v = this.normalizeUpper(value);
      return (
        v.includes('NOT AVAILABLE') ||
        v.includes('NOT RELEASED') ||
        v.includes('HOLD') ||
        v.includes('HELD') ||
        v.includes('CUST EXAM')
      );
    },
    isPositiveStatus(value: any): boolean {
      const v = this.normalizeUpper(value);
      return (
        v.includes('AVAILABLE') ||
        v.includes('RELEASED') ||
        v.includes('RELEASE') ||
        v.includes('CLEARED') ||
        v.includes('OFF-DOCK') ||
        v.includes('OFF DOCK')
      );
    },
    isClearedCustoms(customsStatus: any) {
      return (
        customsStatus.includes('CLEARED') ||
        customsStatus.includes('Released') ||
        customsStatus.includes('Release') ||
        customsStatus.includes('Cleared') ||
        customsStatus.includes('Released')
      );
    },
    normalizeValue(value: any): string {
      if (value === null || value === undefined) return '';
      return String(value).trim();
    },
    hasChanged(field: string): boolean {
      const vm = this as any;
      const current = vm.row?.event;
      const prev = vm.previousRow?.event;
      if (!current || !prev) return false;

      return vm.normalizeValue(current[field]) !== vm.normalizeValue(prev[field]);
    },
    hasFieldChangedClass(field: string): string {
      return this.hasChanged(field) ? 'cte-changed' : '';
    },
  },
  computed: {
    rowStatusClass(): string {
      const vm = this as any;
      const yardStatus = vm.row?.event?.['yard_status'];
      const customs = vm.row?.event?.['customs'];

      if (vm.terminal === 'ETS') {
        return 'cte-row-terminal-ets';
      }
      if (vm.terminal === 'TRAPAC') {
        return 'cte-row-terminal-trapac';
      }
      const hasNegative = vm.isNegativeStatus(yardStatus) || vm.isNegativeStatus(customs);
      if (hasNegative) return 'cte-row-negative';

      const hasPositive = vm.isPositiveStatus(yardStatus) || vm.isPositiveStatus(customs);
      if (hasPositive) return 'cte-row-positive';

      return '';
    },
    collapseTooltip(): string {
      const vm = this as any;
      const count = Number(vm.row?.collapsedCount || 0);
      if (count <= 0) return '';

      const diffs = vm.row?.collapseDiffs || {};
      const diffKeys = Object.keys(diffs);

      const sanitize = (val: string) =>
        String(val)
          .replace(/\s+/g, ' ')
          .replace(/[:;]/g, '')
          .trim();

      const header = sanitize(
        `Collapsed ${count} matching update${count === 1 ? '' : 's'} (displayed columns matched)`
      );

      let body = '';
      if (diffKeys.length > 0) {
        const parts = diffKeys
          .sort()
          .map((k) => {
            const d = diffs[k] || {};
            const from = sanitize(vm.normalizeValue(d.from) || '∅');
            const to = sanitize(vm.normalizeValue(d.to) || '∅');
            return sanitize(`${k} ${from} -> ${to}`);
          });
        body = ` Hidden diffs ${parts.join(' | ')}`;
      } else {
        body = ' Hidden diffs none';
      }

      // UIkit parses uk-tooltip as an options string. Return a valid option list.
      return `title: ${header}.${sanitize(body)}; pos: right`;
    },
  },
});
</script>

<style scoped>
 .cte-date {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
 }

 .cte-collapse-icon {
  display: inline-flex;
  align-items: center;
  color: rgba(100, 116, 139, 0.95);
 }

 .cte-row td {
  vertical-align: top;
 }

.cte-row.cte-row-positive td {
  background-color: #eaf7ea !important;
}

.cte-row.cte-row-negative td {
  background-color: #fdecec !important;
}

.uk-label.cte-changed {
  outline: 2px solid #0f172a;
  outline-offset: 1px;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.85);
}

.cte-changed {
  font-weight: 600;
  text-decoration: underline dotted #0f172a;
  text-decoration-thickness: 2px;
  text-underline-offset: 2px;
}
</style>
